import { useEffect, useState } from "react";

// material-ui

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputLabel,
  Stack,
  TextField,
  FormControlLabel,
  Switch,
  FormControl,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";

// third-party
import _ from "lodash";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";

// project imports

import CircularWithPath from "components/@extended/progress/CircularWithPath";

import { openSnackbar } from "api/snackbar";

// assets

// types
import { SnackbarProps } from "types/snackbar";
import { useAppDispatch } from "hooks";
import { UserList } from "types/customer";

import AlertCompanyDelete from "./AlertCompanyDelete";
import { FormattedMessage, useIntl } from "react-intl";
import { AXIOS } from "../../../../../api/axios";

// constant
const getInitialValues = (customer: any | null) => {
  if (customer) {
    return {
      name: customer.name || "",
      name_trans: customer.name_trans.ar || "",
      order: customer.order || 0,
      is_active: customer.is_active || false,
    };
  } else {
    return {
      name: "",
      name_trans: "",
      order: 0,
      is_active: false,
    };
  }
};

// ==============================|| COMPANY AVTIVITY ADD / EDIT - FORM ||============================== //

const FormCompanyAdd = ({
  customer,
  id,
  closeModal,
  fetchData,
  setResult,
}: {
  fetchData: any;
  id: number;
  customer: UserList | null;
  closeModal: () => void;
  setResult: any;
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(true);
  const intl = useIntl();

  useEffect(() => {
    setLoading(false);
  }, []);

  const CustomerSchema = Yup.object().shape({
    name: Yup.string().required("English Name is required"),
    name_trans: Yup.string().required("Arabic Name is required"),
    order: Yup.string().required("Order is required"),
    is_active: Yup.string().required("Status is required"),
  });

  const [openAlert, setOpenAlert] = useState(false);

  const handleAlertClose = () => {
    setOpenAlert(!openAlert);
    closeModal();
  };

  const formik = useFormik({
    initialValues: getInitialValues(customer!),
    validationSchema: CustomerSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          name: values.name,
          name_trans: {
            ar: values.name_trans,
          },
          order: values.order,
          is_active: values.is_active,
        };

        if (customer) {
          // Edit existing company
          const response = await AXIOS.post(
            `/company_activities/${customer.id}`,
            payload
          );
          setResult((prevResults) =>
            prevResults.map((item) =>
              item.id === response.data.data.id ? response.data.data : item
            )
          );
          openSnackbar({
            open: true,
            message: "Company activity updated successfully",
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "alert",
            alert: {
              color: "success",
            },
          } as SnackbarProps);
        } else {
          // Add new company
          const response = await AXIOS.post("/company_activities", payload);
          setResult((prevResults) => [response.data.data, ...prevResults]);
          openSnackbar({
            open: true,
            message: "Company activity added successfully",
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "alert",
            alert: {
              color: "success",
            },
          } as SnackbarProps);
        }

        closeModal();
      } catch (error) {
        console.error("Error submitting form:", error);
        openSnackbar({
          open: true,
          message: "An error occurred while saving the company",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "error",
          },
        } as SnackbarProps);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
  } = formik;
  const { value } = getFieldProps("");
  if (loading)
    return (
      <Box sx={{ p: 5 }}>
        <Stack direction="row" justifyContent="center">
          <CircularWithPath />
        </Stack>
      </Box>
    );

  return (
    <>
      <FormikProvider value={formik}>
        <LocalizationProvider>
          <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
            <DialogTitle
              style={{
                textAlign: "center",
                fontSize: "22px",
                fontWeight: "bold",
                color: "rgb(22, 119, 255)",
                marginBottom: "10px",
              }}
            >
              {customer ? (
                <FormattedMessage id="Edit Company Activity" />
              ) : (
                <FormattedMessage id="New Company Activity" />
              )}
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 2.5 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="english-name">
                          <FormattedMessage id="English Name" />
                        </InputLabel>
                        <TextField
                          fullWidth
                          id="english-name"
                          placeholder={intl.formatMessage({
                            id: "Enter.activity.english.name",
                            defaultMessage: "Enter activity english name",
                          })}
                          {...getFieldProps("name")}
                          error={Boolean(touched.name && errors.name)}
                          helperText={touched.name && errors.name}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="name_trans">
                          <FormattedMessage id="Arabic Name" />
                        </InputLabel>
                        <TextField
                          fullWidth
                          id="name_trans"
                          placeholder={intl.formatMessage({
                            id: "Enter.activity.arabic.name",
                            defaultMessage: "Enter activity arabic name",
                          })}
                          {...getFieldProps("name_trans")}
                          error={Boolean(
                            touched.name_trans && errors.name_trans
                          )}
                          helperText={touched.name_trans && errors.name_trans}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customer-order">
                          <FormattedMessage id="Order" />{" "}
                          <span style={{ color: "red" }}>*</span>
                        </InputLabel>
                        <TextField
                          fullWidth
                          type="number"
                          id="customer-order"
                          defaultValue={0}
                          placeholder={intl.formatMessage({
                            id: "enter.order",
                            defaultMessage: "Enter order",
                          })}
                          {...getFieldProps("order")}
                          error={Boolean(touched.order && errors.order)}
                          helperText={touched.order && errors.order}
                        />
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customer-is_active">
                          <FormattedMessage id="Status" />
                        </InputLabel>
                        <FormControl fullWidth>
                          <FormControlLabel
                            control={
                              <Switch
                                id="customer-is_active"
                                checked={formik.values.is_active}
                                {...getFieldProps("is_active")}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setFieldValue("is_active", checked);
                                }}
                              />
                            }
                            label={
                              formik.values.is_active ? (
                                <FormattedMessage id="Active" />
                              ) : (
                                <FormattedMessage id="Inactive" />
                              )
                            }
                          />
                        </FormControl>
                        {touched.is_active && errors.is_active && (
                          <FormHelperText
                            error
                            id="standard-weight-helper-text-email-login"
                            sx={{ pl: 1.75 }}
                          >
                            {errors.is_active}
                          </FormHelperText>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </DialogContent>
            <Divider />

            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="center" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                  <Grid item md={2} />
                  <Grid item md={4}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                    >
                      {customer ? <FormattedMessage id="Update" /> : "Submit"}
                    </Button>
                  </Grid>
                  <Grid item md={4}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={closeModal}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Grid>
                  <Grid item md={2} />
                </Stack>
              </Grid>
            </DialogActions>
          </Form>
        </LocalizationProvider>
      </FormikProvider>
      {customer && (
        <AlertCompanyDelete
          fetchData={fetchData}
          id={customer.id!}
          title={customer.name}
          open={openAlert}
          handleClose={handleAlertClose}
        />
      )}
    </>
  );
};

export default FormCompanyAdd;
